import { Calendar as CalendarIcon, MapPin, ThumbsUp, Trash2 } from "lucide-react";
import { formatDate } from "../../ui";

const STATUS_STYLE = {
  agendada: "bg-[#002776]/8 text-[#002776] border-[#002776]/30",
  realizada: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelada: "bg-rose-50 text-rose-700 border-rose-200",
};

export const MeetingStatusPill = ({ status }) => (
  <span
    className={`px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-wide ${
      STATUS_STYLE[status] || "bg-slate-100 text-slate-700 border-slate-200"
    }`}
  >
    {status}
  </span>
);

const MeetingMeta = ({ m }) => (
  <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wide">
    <MeetingStatusPill status={m.status} />
    <span className="text-slate-500">{formatDate(m.meeting_date)}</span>
    {(m.start_time || m.end_time) && (
      <span className="text-slate-500">
        · {m.start_time || "—"}
        {m.end_time ? ` – ${m.end_time}` : ""}
      </span>
    )}
    {m.location && (
      <span className="inline-flex items-center gap-1 text-slate-500">
        <MapPin size={11} /> {m.location}
      </span>
    )}
    {m.is_online && (
      <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">● Online</span>
    )}
  </div>
);

const LinkedPautas = ({ m }) => (
  <div className="mt-3">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">
      Pautas designadas ({m.pautas_count})
    </div>
    {m.pautas && m.pautas.length > 0 ? (
      <ul className="mt-2 space-y-1">
        {m.pautas.map((p) => (
          <li key={p.id} className="text-sm text-slate-800 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 text-slate-700 text-[10px] rounded-sm">
              <ThumbsUp size={10} /> {p.vote_count}
            </span>
            {p.title}
          </li>
        ))}
      </ul>
    ) : (
      <div className="mt-2 text-xs text-slate-500 italic">Nenhuma pauta vinculada ainda.</div>
    )}
  </div>
);

const AdminButtons = ({ m, onEdit, onDelete }) => (
  <div className="flex md:flex-col gap-2">
    <button data-testid={`meeting-edit-${m.id}`} onClick={() => onEdit(m)}
      className="px-3 py-1.5 text-[10px] uppercase tracking-wide border border-slate-300 text-slate-700 hover:border-[#002776]">
      Editar
    </button>
    <button data-testid={`meeting-delete-${m.id}`} onClick={() => onDelete(m)}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-wide border border-rose-300 text-rose-700 hover:bg-rose-50">
      <Trash2 size={11} /> Excluir
    </button>
  </div>
);

export const MeetingCard = ({ m, isAdmin, onEdit, onDelete }) => (
  <li data-testid={`meeting-row-${m.id}`} className="bg-white border border-slate-200 p-5 rounded-md">
    <div className="flex flex-col md:flex-row md:items-start gap-4">
      <div className="h-14 w-14 grid place-items-center bg-[#002776]/8 border border-[#002776]/30 text-[#002776] rounded-sm shrink-0">
        <CalendarIcon size={22} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <MeetingMeta m={m} />
        <h3 className="mt-2 font-serif-display text-xl">{m.title}</h3>
        {m.is_online && m.online_link && (
          <a
            data-testid={`meeting-online-link-${m.id}`}
            href={m.online_link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 border border-[#002776] text-[#002776] text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] hover:text-white transition-colors"
          >
            Entrar na reunião
          </a>
        )}
        {m.description && (
          <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">{m.description}</p>
        )}
        <LinkedPautas m={m} />
      </div>
      {isAdmin && <AdminButtons m={m} onEdit={onEdit} onDelete={onDelete} />}
    </div>
  </li>
);
